import { ApiResponse } from '../../shared/types';
import { RegisteredDevice } from '../types/device';
import { supabase } from './supabase';

/**
 * Fetch all registered devices for an outlet or across the company
 */
export const fetchDevices = async (
  outletId?: string | null,
): Promise<ApiResponse<RegisteredDevice[]>> => {
  try {
    let query = supabase.from('registered_devices').select('*');
    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    const { data, error } = await query.order('created_at', {
      ascending: false,
    });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return { success: true, data: data as RegisteredDevice[] };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch devices',
      data: [],
    };
  }
};

/**
 * Toggle the allowed/disallowed status of a device
 */
export const toggleDeviceEnabled = async (
  deviceId: string,
  isEnabled: boolean,
): Promise<ApiResponse<RegisteredDevice>> => {
  try {
    const { data, error } = await supabase
      .from('registered_devices')
      .update({ is_enabled: isEnabled })
      .eq('id', deviceId)
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: data as RegisteredDevice };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to toggle device state',
      data: undefined,
    };
  }
};

/**
 * Permanently delete/unregister a device
 */
export const deleteDevice = async (
  deviceId: string,
): Promise<ApiResponse<void>> => {
  try {
    const { error } = await supabase
      .from('registered_devices')
      .delete()
      .eq('id', deviceId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete device',
    };
  }
};
